import { IoRocketOutline } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import colors from 'tailwindcss/colors'

import { useModal } from '@/hooks/useModal'
import { ReduxProps } from '@/storage'
import { CampaignsStateProps } from '@/storage/modules/campaigns/reducer'
import { CampaignDataProps } from '@/types/campaignProps'

import { EmptyState } from '../EmptyState'
import { FormCampaign } from '../FormCampaign'
import { FormPublishCampaign } from '../FormPublishCampaign'

function formatDateTime(value?: string | null) {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

function resolveStatusColor(status: CampaignDataProps['status']) {
  switch (status) {
    case 'SENT':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'PARTIAL':
      return 'bg-amber-100 text-amber-700 border-amber-200'
    case 'FAILED':
      return 'bg-red-100 text-red-700 border-red-200'
    case 'PROCESSING':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'DISABLED':
    case 'EXPIRED':
      return 'bg-gray-100 text-gray-700 border-gray-200'
    default:
      return 'bg-purple-100 text-purple-700 border-purple-200'
  }
}

export function Campaigns() {
  const { openModal } = useModal()
  const { campaigns } = useSelector<ReduxProps, CampaignsStateProps>(
    (state) => state.campaigns,
  )

  if (campaigns.length === 0) {
    return (
      <EmptyState
        title="No campaigns yet"
        description="Create the first campaign draft to prepare publication."
      />
    )
  }

  return (
    <div className="space-y-6 pt-6">
      {campaigns.map((campaign) => {
        const startsAt = formatDateTime(campaign.startsAt)
        const expiresAt = formatDateTime(campaign.expiresAt)
        const createdAt = formatDateTime(campaign.createdAt)

        return (
          <div
            key={campaign.id}
            className="rounded-2xl border bg-white p-6 transition-colors hover:border-gray-300"
          >
            <div className="flex items-start justify-between gap-4 md:flex-col">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 md:flex-wrap">
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${resolveStatusColor(
                      campaign.status,
                    )}`}
                  >
                    {campaign.status}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                    {campaign.type}
                  </span>
                </div>

                <h3 className="mt-3 truncate text-lg font-bold text-gray-700">
                  {campaign.title}
                </h3>
                <p className="mt-2 text-sm text-gray-500">{campaign.body}</p>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-gray-500 md:grid-cols-1">
                  <p>CTA: {campaign.ctaType}</p>
                  <p>
                    Metrics: {campaign.pushSuccessCount ?? 0}/
                    {campaign.pushAttemptedCount ?? 0} push accepted
                  </p>
                  {startsAt && <p>Starts at: {startsAt}</p>}
                  {expiresAt && <p>Expires at: {expiresAt}</p>}
                  {createdAt && <p>Created at: {createdAt}</p>}
                  {campaign.ctaValue && <p>CTA value: {campaign.ctaValue}</p>}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-3 md:w-full">
                <button
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 md:flex-1"
                  onClick={() =>
                    openModal({
                      small: true,
                      children: <FormCampaign campaign={campaign} />,
                    })
                  }
                >
                  Edit
                </button>

                {campaign.status === 'DRAFT' && (
                  <button
                    className="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-100 md:flex-1 md:justify-center"
                    onClick={() =>
                      openModal({
                        small: true,
                        children: <FormPublishCampaign campaign={campaign} />,
                      })
                    }
                  >
                    <IoRocketOutline color={colors.purple[700]} />
                    Publish
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
